import {
  createContext,
  useState,
  type ReactNode,
  useRef,
  useEffect,
  useCallback,
  useMemo
} from "react";

import { ReactPortal } from "../ReactPortal";

type ModalContent = ReactNode | null;

type ModalProps = {
  component: ReactNode;
};

export type ModalContextType = {
  showModal: (args: ModalProps) => void;
  hideModal: () => void;
};

export const ModalContext = createContext<ModalContextType | undefined>(
  undefined
);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalContent, setModalContent] = useState<ModalContent>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const showModal = useCallback(({ component }: ModalProps) => {
    setModalContent(component);
  }, []);

  const hideModal = useCallback(() => {
    setModalContent(null);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        hideModal();
      }
    };

    if (modalContent) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [hideModal, modalContent]);

  const value = useMemo(
    () => ({ showModal, hideModal }),
    [hideModal, showModal]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      {modalContent ? (
        <ReactPortal wrapperId="modal-root">
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={hideModal}
            />
            {/* Modal */}
            <div
              ref={modalRef}
              className="fixed left-1/2 top-1/2 max-h-[90vh] w-full -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-lg bg-white p-6 shadow-lg sm:max-w-[90%] lg:max-w-[60%]"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {modalContent}
            </div>
          </div>
        </ReactPortal>
      ) : null}
    </ModalContext.Provider>
  );
};
